import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from 'react-router'
import styles from './index.module.scss'
import PlusIcon from "../../assets/icon/PlusIcon";
import ProjectModal from "../../components/Modal/components/ProjectModal";
import ProjectService from "../../services/project.service";
import { Project } from "../../types";
import Header from "../../components/Header";
import { useWorkspace } from "../../context/workspace.context";
import DropDown from "../../components/DropDown";
import DotsVerticalIcon from "../../assets/icon/DotsVerticalIcon";
import CopyLinkIcon from "../../assets/icon/CopyLinkIcon";
import TrashIcon from "../../assets/icon/TrashIcon";
import ProjectDuplicateIcon from "../../assets/icon/ProjectDuplicateIcon";
import ProjectStatusesIcon from "../../assets/icon/ProjectStatusesIcon";
import ArchiveIcon from "../../assets/icon/ArchiveIcon";
import FilterCloseIcon from "../../assets/icon/FilterCloseIcon";
import ProjectStatusModal from '../../components/ProjectControls/ProjectStatusModal';
import ProjectDeleteModal from '../../components/ProjectControls/ProjectDeleteModal';
import ProjectDuplicateModal from '../../components/ProjectControls/ProjectDuplicateModal';
import { ProjectTaskStatusItem } from '../../utils/projectStatus';
import { copyProjectLink } from '../../utils/projectLink'
import EmptyFolderIcon from '../../assets/icon/EmptyFolderIcon';
import SuccessNotificationIcon from '../../assets/icon/SuccessNotificationIcon';
import PrivacySettingsIcon from '../../assets/icon/PrivacySettingsIcon';
import ShareProjectModal from '../../components/ShareProjectModal';
import EditIcon from '../../assets/icon/EditIcon';
import WorkspaceModal from '../../components/Modal/components/WorkspaceModal';
import WorkspaceRequiredModal from '../../components/WorkspaceRequiredModal';
import { PROJECTS_PAGE_URI } from '../../constants/uri.constant';
import {
    isSameProjectOrder,
    mergeProjectOrder,
    reorderProjects,
    type ProjectDropPosition,
} from './projectOrder';

function isInteractiveDragTarget(target: EventTarget | null) {
    return target instanceof Element && Boolean(target.closest('button, input, textarea, select, a, [role="button"], [data-no-project-drag]'))
}

function isArchivedProject(project: Project) {
    return Boolean(project?.is_archived || project?.archived_at)
}

export default function ProjectList() {
    const [projects, setProjects] = useState<Project[]>([])
    const [isProjectCreateOpen, setIsProjectCreateOpen] = useState(false)
    const [isWorkspaceCreateOpen, setIsWorkspaceCreateOpen] = useState(false)
    const [isWorkspaceRequiredOpen, setIsWorkspaceRequiredOpen] = useState(false)
    const [deleteProject, setDeleteProject] = useState<Project | null>(null)
    const [duplicateProject, setDuplicateProject] = useState<Project | null>(null)
    const [statusProject, setStatusProject] = useState<Project | null>(null)
    const [privacyProject, setPrivacyProject] = useState<Project | null>(null)
    const [renamingProjectId, setRenamingProjectId] = useState<number | null>(null)
    const [renameName, setRenameName] = useState('')
    const [statusModalStatuses, setStatusModalStatuses] = useState<ProjectTaskStatusItem[]>([])
    const [successNotification, setSuccessNotification] = useState<{ title: string, text: string } | null>(null)
    const [showArchived, setShowArchived] = useState(false)
    const [draggedProjectId, setDraggedProjectId] = useState<number | null>(null)
    const [projectDropTarget, setProjectDropTarget] = useState<{ id: number, position: ProjectDropPosition } | null>(null)
    const [isOrderSaving, setIsOrderSaving] = useState(false)
    const { workspace: contextWorkspace, workspaces, setWorkspace } = useWorkspace()
    const location = useLocation()
    const navigate = useNavigate()
    const renameInputRef = useRef<HTMLTextAreaElement | null>(null)
    const renameSaveInFlightRef = useRef(false)
    const skipRenameBlurRef = useRef(false)
    const draggedProjectIdRef = useRef<number | null>(null)
    const dragProjectsRef = useRef<Project[]>([])
    const projectsBeforeDragRef = useRef<Project[]>([])
    const didDropRef = useRef(false)
    const suppressProjectClickRef = useRef(false)
    const requestedWorkspaceId = (location.state as { workspaceId?: string | number } | null)?.workspaceId
    const requestedWorkspace = requestedWorkspaceId == null
        ? null
        : workspaces.find((item) => String(item.id) === String(requestedWorkspaceId)) || null
    const workspace = requestedWorkspace || contextWorkspace

    useEffect(() => {
        if (!requestedWorkspace) {
            return
        }

        if (String(contextWorkspace?.id) !== String(requestedWorkspace.id)) {
            setWorkspace(requestedWorkspace)
            return
        }

        // The target workspace has been applied. Remove the one-shot route
        // state so later switches made directly from the list are not reverted.
        navigate(PROJECTS_PAGE_URI, { replace: true, state: null })
    }, [contextWorkspace?.id, navigate, requestedWorkspace, setWorkspace])

    useEffect(() => {
        let isCurrentRequest = true

        const loadProjects = async () => {
            if (!workspace) {
                setProjects([])
                return
            }

            try {
                const [activeList, archivedList] = await Promise.all([
                    ProjectService.list(),
                    ProjectService.listArchived().catch(() => ({ projects: [] })),
                ])
                const active = (activeList.projects || activeList || []) as Project[]
                const archived = (archivedList.projects || archivedList || []) as Project[]
                const merged = new Map<number, Project>()

                active.forEach((project) => merged.set(project.id, project))
                archived.forEach((project) => merged.set(project.id, {
                    ...project,
                    is_archived: true,
                    archived_at: project.archived_at ?? new Date().toISOString(),
                }))

                if (isCurrentRequest) {
                    setProjects(Array.from(merged.values()).filter(
                        (project) => String(project.workspace_id) === String(workspace.id)
                    ))
                }
            } catch (error) {
                console.error('Failed to load projects:', error)
            }
        }

        loadProjects()

        return () => {
            isCurrentRequest = false
        }
    }, [workspace])

    useEffect(() => {
        if (!successNotification) {
            return
        }

        const timeoutId = setTimeout(() => {
            setSuccessNotification(null)
        }, 5000)

        return () => clearTimeout(timeoutId)
    }, [successNotification])

    const showSuccessNotification = useCallback((text: string) => {
        setSuccessNotification({
            title: 'Успешно',
            text,
        })
    }, [])

    const createProject = async (projectData: any) => {
        if (!workspace?.id) {
            setIsProjectCreateOpen(false)
            setIsWorkspaceRequiredOpen(true)
            return
        }

        const newProject = await ProjectService.create({
            workspace_id: workspace.id,
            ...projectData,
        })

        setProjects((prev) => [...prev, newProject])
        setIsProjectCreateOpen(false)
    }

    const openProjectCreation = () => {
        if (!workspace?.id) {
            setIsWorkspaceRequiredOpen(true)
            return
        }
        setIsProjectCreateOpen(true)
    }

    const openWorkspaceCreation = () => {
        setIsWorkspaceRequiredOpen(false)
        setIsWorkspaceCreateOpen(true)
    }

    const handleProjectClick = (projectId: number) => {
        navigate(`/projects/${projectId}`)
    }

    const startRename = (project: Project) => {
        skipRenameBlurRef.current = false
        setRenamingProjectId(project.id)
        setRenameName(project.name || '')
        window.setTimeout(() => {
            const input = renameInputRef.current
            if (!input) return

            input.focus()
            input.setSelectionRange(input.value.length, input.value.length)
        }, 0)
    }

    const cancelRename = () => {
        skipRenameBlurRef.current = true
        setRenamingProjectId(null)
        setRenameName('')
    }

    const confirmRename = async (projectId: number) => {
        if (renameSaveInFlightRef.current) return

        const name = renameName.trim()

        if (!name) {
            cancelRename()
            return
        }

        const currentProject = projects.find((project) => project.id === projectId)
        if (name === String(currentProject?.name || '').trim()) {
            cancelRename()
            return
        }

        renameSaveInFlightRef.current = true
        try {
            const updatedProject = await ProjectService.update(String(projectId), { name })
            setProjects((prev) => prev.map((project) => (
                project.id === projectId
                    ? { ...project, ...(updatedProject || {}), name }
                    : project
            )))
            cancelRename()
        } catch (error) {
            console.error('Failed to rename project:', error)
            window.setTimeout(() => renameInputRef.current?.focus(), 0)
        } finally {
            renameSaveInFlightRef.current = false
        }
    }

    useEffect(() => {
        const input = renameInputRef.current
        if (renamingProjectId == null || !input) return

        input.style.height = '0'
        input.style.height = `${Math.min(input.scrollHeight, 48)}px`
    }, [renameName, renamingProjectId])

    const handleDelete = async () => {
        if (!deleteProject) return

        try {
            await ProjectService.delete(String(deleteProject.id))
            setProjects((prev) => prev.filter((project) => project.id !== deleteProject.id))
            setDeleteProject(null)
        } catch (error) {
            console.error('Failed to delete project:', error)
        }
    }

    const handleDuplicate = async ({ name, color, copyContent }: { name: string, color: string, copyContent: boolean }) => {
        if (!duplicateProject) return

        try {
            const newProject = await ProjectService.duplicateProject(duplicateProject, name, undefined, {
                color,
                copyContent,
            })
            setProjects((prev) => [...prev, newProject])
            setDuplicateProject(null)
            showSuccessNotification('Проект успешно продублирован')
        } catch (error) {
            console.error('Failed to duplicate project:', error)
        }
    }

    const handleOpenStatusModal = async (project: Project) => {
        try {
            const statuses = await ProjectService.getSortedTaskStatuses(String(project.id))
            setStatusModalStatuses(statuses)
            setStatusProject(project)
        } catch (error) {
            console.error('Failed to fetch task statuses:', error)
        }
    }

    const handleCopyProjectLink = async (projectId: number) => {
        try {
            await copyProjectLink(projectId)
            showSuccessNotification('Ссылка скопирована')
        } catch (error) {
            console.error('Failed to copy project link:', error)
        }
    }

    const handleCloseStatusModal = () => {
        setStatusProject(null)
        setStatusModalStatuses([])
    }

    const handleArchiveProject = async (project: Project) => {
        const wasArchived = isArchivedProject(project)

        try {
            const updatedProject = wasArchived
                ? await ProjectService.unarchive(project.id)
                : await ProjectService.archive(project.id)

            setProjects((prev) => prev.map((current) => (
                current.id === project.id
                    ? { ...current, ...(updatedProject || {}), is_archived: !wasArchived, archived_at: !wasArchived ? (updatedProject?.archived_at || new Date().toISOString()) : null }
                    : current
            )))

            showSuccessNotification(wasArchived ? 'Проект возвращён из архива' : 'Проект перенесён в архив')
        } catch (error) {
            console.error('Failed to change project archive state:', error)
        }
    }

    const visibleProjects = projects.filter((project) => showArchived ? isArchivedProject(project) : !isArchivedProject(project))

    const resetProjectDrag = () => {
        draggedProjectIdRef.current = null
        dragProjectsRef.current = []
        projectsBeforeDragRef.current = []
        setDraggedProjectId(null)
        setProjectDropTarget(null)
    }

    const handleProjectDragStart = (event: React.DragEvent<HTMLDivElement>, projectId: number) => {
        if (isOrderSaving || renamingProjectId != null || isInteractiveDragTarget(event.target)) {
            event.preventDefault()
            return
        }

        const currentVisibleProjects = projects.filter((project) => showArchived ? isArchivedProject(project) : !isArchivedProject(project))
        draggedProjectIdRef.current = projectId
        dragProjectsRef.current = currentVisibleProjects
        projectsBeforeDragRef.current = projects
        didDropRef.current = false
        suppressProjectClickRef.current = true
        setDraggedProjectId(projectId)
        setProjectDropTarget(null)
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', String(projectId))
    }

    const handleProjectDragOver = (event: React.DragEvent<HTMLDivElement>, targetProjectId: number) => {
        const currentDraggedId = draggedProjectIdRef.current
        if (currentDraggedId == null) return

        event.preventDefault()
        event.stopPropagation()
        event.dataTransfer.dropEffect = 'move'

        // After the live reorder the dragged card can move underneath the
        // pointer. The browser still needs preventDefault above to allow the
        // subsequent drop, even though there is nothing else to reorder.
        if (currentDraggedId === targetProjectId) return

        const rect = event.currentTarget.getBoundingClientRect()
        const position: ProjectDropPosition = event.clientX - rect.left > rect.width / 2 ? 'after' : 'before'
        const nextVisibleProjects = reorderProjects(dragProjectsRef.current, currentDraggedId, targetProjectId, position)

        if (nextVisibleProjects === dragProjectsRef.current) {
            return
        }

        dragProjectsRef.current = nextVisibleProjects
        setProjects((currentProjects) => mergeProjectOrder(currentProjects, nextVisibleProjects))
        setProjectDropTarget({ id: targetProjectId, position })
    }

    const handleProjectDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        if (draggedProjectIdRef.current == null || !workspace?.id) return

        event.preventDefault()
        event.stopPropagation()
        didDropRef.current = true

        const previousProjects = projectsBeforeDragRef.current
        const nextVisibleProjects = dragProjectsRef.current
        const previousVisibleProjects = previousProjects.filter((project) => (
            showArchived ? isArchivedProject(project) : !isArchivedProject(project)
        ))
        const orderChanged = !isSameProjectOrder(previousVisibleProjects, nextVisibleProjects)
        resetProjectDrag()

        if (!orderChanged) {
            window.setTimeout(() => {
                suppressProjectClickRef.current = false
            }, 0)
            return
        }

        setIsOrderSaving(true)

        try {
            await ProjectService.reorder(workspace.id, nextVisibleProjects.map((project) => project.id), showArchived)
        } catch (error) {
            console.error('Failed to persist project order:', error)
            setProjects(previousProjects)
        } finally {
            setIsOrderSaving(false)
            window.setTimeout(() => {
                suppressProjectClickRef.current = false
            }, 0)
        }
    }

    const handleProjectDragEnd = () => {
        if (!didDropRef.current && projectsBeforeDragRef.current.length) {
            setProjects(projectsBeforeDragRef.current)
        }

        didDropRef.current = false
        resetProjectDrag()
        window.setTimeout(() => {
            suppressProjectClickRef.current = false
        }, 0)
    }

    return (
        <>
            <Header />
            <div className={styles.projects}>
                <div className={styles.projects__title}>
                    <nav className={styles.projects__breadcrumbs} aria-label="Хлебные крошки">
                        <button
                            type="button"
                            className={styles.projects__breadcrumbLink}
                            title={workspace?.name || 'Рабочее пространство'}
                            onClick={() => navigate('/workspaces')}
                        >
                            {workspace?.name || 'Рабочее пространство'}
                        </button>
                        <span className={styles.projects__breadcrumbSeparator}>/</span>
                        <span className={styles.projects__breadcrumbCurrent}>Проекты</span>
                    </nav>
                    <div className={styles.projects__title_action}>
                        <button
                            type="button"
                            className={`${styles.projects__filterBtn} ${showArchived ? styles.projects__filterBtnActive : ''}`}
                            onClick={() => setShowArchived((prev) => !prev)}
                        >
                            <span className={styles.projects__filterIcon}>
                                <ArchiveIcon />
                            </span>
                            <span>Архивированные</span>
                            {showArchived && (
                                <span className={styles.projects__filterClose}>
                                    <FilterCloseIcon />
                                </span>
                            )}
                        </button>
                        <button
                            type="button"
                            className={styles.projects__title_action_btn}
                            onClick={openProjectCreation}
                        >
                            <PlusIcon />
                            <span>
                                Создать проект
                            </span>
                        </button>
                    </div>
                </div>
                {visibleProjects.length === 0 && (
                    <div className={styles.projects__empty}>
                        <div className={styles.projects__emptyIcon}>
                            <EmptyFolderIcon />
                        </div>
                        <p className={styles.projects__emptyText}>
                            {showArchived ? 'В архиве нет проектов' : 'У вас еще нет проектов'}
                        </p>
                        {!showArchived && (
                            <button
                                type="button"
                                className={styles.projects__emptyBtn}
                                onClick={openProjectCreation}
                            >
                                Создать проект
                            </button>
                        )}
                    </div>
                )}

                <div className={styles.projects__scroll}>
                <div
                    className={styles.projects__wrapper}
                    onDragOver={(event) => {
                        if (draggedProjectIdRef.current != null) event.preventDefault()
                    }}
                    onDrop={handleProjectDrop}
                >
                    {visibleProjects.map((project) => (
                        <div
                            className={`${styles.projects__item} ${draggedProjectId === project.id ? styles.projects__itemDragging : ''} ${projectDropTarget?.id === project.id && projectDropTarget.position === 'before' ? styles.projects__itemDropBefore : ''} ${projectDropTarget?.id === project.id && projectDropTarget.position === 'after' ? styles.projects__itemDropAfter : ''}`}
                            key={project.id}
                            draggable={!isOrderSaving && renamingProjectId !== project.id}
                            onClick={() => {
                                if (!suppressProjectClickRef.current) handleProjectClick(project.id)
                            }}
                            onDragStart={(event) => handleProjectDragStart(event, project.id)}
                            onDragOver={(event) => handleProjectDragOver(event, project.id)}
                            onDrop={handleProjectDrop}
                            onDragEnd={handleProjectDragEnd}
                        >
                            <img src="/items/project-folder.png" className={styles.projects__item_img} alt="project folder" />
                            <div className={styles.projects__block}>
                                <div className={styles.projects__block_top}>
                                    <div className={styles.projects__item_color} style={{ borderColor: project.color }} />
                                    {renamingProjectId === project.id ? (
                                        <div className={styles.projects__renameWrap} onClick={(event) => event.stopPropagation()}>
                                            <textarea
                                                ref={renameInputRef}
                                                className={styles.projects__renameInput}
                                                value={renameName}
                                                rows={1}
                                                aria-label="Название проекта"
                                                onChange={(event) => setRenameName(event.target.value.replace(/[\r\n]+/g, ' '))}
                                                onBlur={() => {
                                                    if (skipRenameBlurRef.current) {
                                                        skipRenameBlurRef.current = false
                                                        return
                                                    }

                                                    void confirmRename(project.id)
                                                }}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter') {
                                                        event.preventDefault()
                                                        event.currentTarget.blur()
                                                    }
                                                    if (event.key === 'Escape') {
                                                        event.preventDefault()
                                                        cancelRename()
                                                    }
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div className={styles.projects__item_title}>
                                            {project.name}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.projects__block_bottom}>
                                    <div className={styles.projects__item_date}>
                                        {new Date(project.created_at).toLocaleDateString()}
                                    </div>
                                    <div data-no-project-drag onClick={(e) => e.stopPropagation()}>
                                        <DropDown
                                            closeDropDownByPick
                                            position="right"
                                            btn={
                                                <div className={styles.projects__item_more}>
                                                    <DotsVerticalIcon />
                                                </div>
                                            }
                                        >
                                            <div className={styles.projects__dropdown}>
                                                <div className={styles.projects__dropdownItem} onClick={() => startRename(project)}>
                                                    <EditIcon />
                                                    <span>Переименовать</span>
                                                </div>
                                                <div className={styles.projects__dropdownItem} onClick={() => setDuplicateProject(project)}>
                                                    <ProjectDuplicateIcon />
                                                    <span>Дублировать</span>
                                                </div>
                                                <div className={styles.projects__dropdownItem} onClick={() => handleCopyProjectLink(project.id)}>
                                                    <CopyLinkIcon />
                                                    <span>Копировать ссылку</span>
                                                </div>
                                                <div className={styles.projects__dropdownItem} onClick={() => setPrivacyProject(project)}>
                                                    <PrivacySettingsIcon />
                                                    <span>Настройки приватности</span>
                                                </div>
                                                <div className={styles.projects__dropdownItem} onClick={() => handleOpenStatusModal(project)}>
                                                    <ProjectStatusesIcon />
                                                    <span>Настройка статусов</span>
                                                </div>
                                                <div className={styles.projects__dropdownItem} onClick={() => handleArchiveProject(project)}>
                                                    <ArchiveIcon />
                                                    <span>{isArchivedProject(project) ? 'Вернуть с архива' : 'Архивировать'}</span>
                                                </div>
                                                <div className={`${styles.projects__dropdownItem} ${styles.projects__dropdownItemDanger}`} onClick={() => setDeleteProject(project)}>
                                                    <TrashIcon />
                                                    <span>Удалить</span>
                                                </div>
                                            </div>
                                        </DropDown>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                </div>
                {isProjectCreateOpen && (
                    <ProjectModal onClose={() => setIsProjectCreateOpen(false)} createProject={createProject} />
                )}
                <WorkspaceRequiredModal
                    isOpen={isWorkspaceRequiredOpen}
                    feature="project"
                    onClose={() => setIsWorkspaceRequiredOpen(false)}
                    onCreateWorkspace={openWorkspaceCreation}
                />
                {isWorkspaceCreateOpen && (
                    <WorkspaceModal
                        onClose={() => setIsWorkspaceCreateOpen(false)}
                        onCreated={(createdWorkspace) => {
                            if (!createdWorkspace?.id) return

                            setWorkspace(createdWorkspace)
                            setIsProjectCreateOpen(true)
                        }}
                    />
                )}
                <ProjectDeleteModal
                    isOpen={!!deleteProject}
                    text={`Вы уверены, что хотите удалить проект «${deleteProject?.name || ''}»? Все задачи будут удалены.`}
                    onClose={() => setDeleteProject(null)}
                    onConfirm={handleDelete}
                />
                <ProjectDuplicateModal
                    isOpen={!!duplicateProject}
                    defaultName={`${duplicateProject?.name || 'Проект'} (копия)`}
                    defaultColor={duplicateProject?.color}
                    onClose={() => setDuplicateProject(null)}
                    onConfirm={handleDuplicate}
                />
                <ProjectStatusModal
                    isOpen={!!statusProject}
                    projectId={statusProject?.id}
                    statuses={statusModalStatuses}
                    onClose={handleCloseStatusModal}
                />
                <ShareProjectModal
                    isOpen={!!privacyProject}
                    project={privacyProject ? {
                        id: privacyProject.id,
                        name: privacyProject.name,
                        color: privacyProject.color,
                        is_public: privacyProject.is_public,
                    } : null}
                    onClose={() => setPrivacyProject(null)}
                    onSaved={({ id, is_public }) => {
                        setProjects((currentProjects) => currentProjects.map((currentProject) => (
                            currentProject.id === id ? { ...currentProject, is_public } : currentProject
                        )))
                    }}
                />
            </div>
                {successNotification && (
                    <div className={styles.projects__notification}>
                        <div className={styles.projects__notificationIcon}>
                            <SuccessNotificationIcon />
                        </div>
                        <div className={styles.projects__notificationContent}>
                            <span className={styles.projects__notificationTitle}>{successNotification.title}</span>
                            <span className={styles.projects__notificationText}>{successNotification.text}</span>
                        </div>
                        <button
                            type="button"
                            className={styles.projects__notificationClose}
                            onClick={() => setSuccessNotification(null)}
                        >
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 1L1 7" stroke="#64617E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M1 1L7 7" stroke="#64617E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                )}
        </>
    )
}
